import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Shield, Lock, AlertTriangle } from "lucide-react";
import { useAuthStore } from "../stores/auth-store";
import { Button } from "../components/ui/button";
import { PasswordInput } from "../components/PasswordInput";
import { Label } from "../components/ui/label";
import { WRAPPED_KEY_MODES } from "../lib/crypto";
import { toast } from "sonner";

export default function UnlockPage() {
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { rederiveKey, logout, activeTenant } = useAuthStore();
  const navigate = useNavigate();
  const isLegacyTenant = activeTenant?.crypto_mode === "legacy_user";
  const isWrappedTenant = WRAPPED_KEY_MODES.includes(activeTenant?.crypto_mode);
  
  const handleUnlock = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const success = await rederiveKey(password);
      
      if (success) {
        toast.success("Cofre desbloqueado!");
        navigate("/dashboard");
      } else {
        toast.error("Senha incorreta ou erro ao derivar chave");
      }
    } catch {
      toast.error("Erro ao desbloquear");
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };
  
  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <Shield className="w-10 h-10 text-secondary" />
          <span className="text-3xl font-bold tracking-tight">KawaFinance</span>
        </div>
        
        <div className="text-center mb-8">
          <Lock className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
          <h1 className="text-2xl font-semibold mb-2">Cofre Bloqueado</h1>
          <p className="text-muted-foreground">
            {isLegacyTenant
              ? "Digite a senha antiga da conta para abrir este cofre migrado"
              : isWrappedTenant
                ? "Digite sua senha de login para liberar a chave do cofre"
                : "Digite a senha do cofre para desbloquear seus dados criptografados"}
          </p>
        </div>
        
        {/* Warning */}
        <div className="alert-banner alert-warning mb-6">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <p className="text-sm">
            {isLegacyTenant
              ? "Este cofre veio de dados existentes e ainda usa a chave antiga do usuário."
              : isWrappedTenant
                ? "A sessão expirou. Sua senha libera localmente a chave criptografada do cofre."
                : "A sessão expirou. A senha do cofre é necessária para descriptografar os dados."}
          </p>
        </div>
        
        <form onSubmit={handleUnlock} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="password">{isLegacyTenant ? "Senha antiga da conta" : isWrappedTenant ? "Senha de login" : "Senha do cofre"}</Label>
            <PasswordInput
              id="password"
              placeholder="••••••••"
              showLockIcon
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="unlock-password"
              autoFocus
            />
          </div>
          
          <Button
            type="submit"
            className="w-full btn-glow"
            disabled={isLoading || !password}
            data-testid="unlock-submit"
          >
            {isLoading ? "Desbloqueando..." : "Desbloquear"}
          </Button>
        </form>
        
        <div className="mt-6 text-center">
          <button
            onClick={handleLogout}
            className="text-sm text-muted-foreground hover:text-destructive transition-colors"
          >
            Sair e usar outra conta
          </button>
        </div>
      </div>
    </div>
  );
}
